import React, { useEffect, useReducer, useState } from 'react';

import {
  Button,
  dlsBrightBlue,
  IconDownload,
  Label,
  Legend,
  RadioButton,
  RadioGroup,
  Select,
  SelectOption,
  Tooltip,
  TooltipButton,
} from '@americanexpress/dls-react';

import PropTypes from 'prop-types';
import { useEnhancedDataTableContext } from '../../EnhancedDataTableContext';
import Loader from '../../../Loader/Loader';
import OverflowItem from '../../../OverflowItem';

const ShareComponent = React.memo((props) => {
  const context = useEnhancedDataTableContext();
  const {
    onExportClick,
    setItemActionClicked,
  } = props;
  const {
    tableHeading,
    headers,
    fetchTableData,
    tableNm,
    defaultColumn,
    tableData,
    overrideExport,
  } = context;

  const { hasDefaultColumn } = defaultColumn;
  const [iconFilled, setIconFilled] = useState(false);
  const [labelComponentClicked, setLabelComponentClicked] = useState(false);
  const [exportInProgress, setExportInProgress] = useState(false);
  const [tableHeaders, setTableHeaders] = useState(headers);
  const [formData, setFormData] = useReducer(
    (value, newDetails) => ({ ...value, ...newDetails }),
    {
      type: 'current',
      format: 'excel',
    }
  );

  useEffect(() => {
    const newTableHeaders = [...headers];
    if (hasDefaultColumn) {
      const defaultHeader = {
        id: 'default',
        display: true,
        label: 'Default',
      };
      newTableHeaders.push(defaultHeader);
    }

    setTableHeaders(newTableHeaders);
  }, [hasDefaultColumn, headers]);

  /* Dynamic import for decreasing bundle size */
  const getFilenameWithDate = (type) => import(/* webpackChunkName: 'moment' */ 'moment').then(
    ({ default: moment }) => {
      const heading = tableHeading?.replace(/\s+/g, '_')
        ?.toLowerCase() || tableData?.tableNm || type;
      const dateString = moment(new Date())
        .format('MMDDYYYYHHmm');
      return `${heading}_${dateString}`;
    }
  );

  const handleOnChange = async (value, fieldName) => {
    const newFormData = { ...formData };
    newFormData[fieldName] = value;
    setFormData(newFormData);
  };

  const handleToolTipBtnClick = () => {
    setLabelComponentClicked(!labelComponentClicked);
    setItemActionClicked(false);
  };

  // eslint-disable-next-line consistent-return
  const exportDataToFile = async () => {
    const {
      type,
      format,
    } = formData;
    setLabelComponentClicked(false);
    setExportInProgress(true);
    if (overrideExport) {
      onExportClick(format);
    } else if (fetchTableData) {
      const { newRows } = await fetchTableData(undefined, true);
      if (newRows) {
        return getFilenameWithDate(format)
          .then(async (filename) => {
            if (format === 'excel') {
              const generateExcel = await import(/* webpackChunkName: 'EnhancedTableExcelGenerator' */ './EnhancedTableExcelGenerator');
              generateExcel.default(
                {
                  title: tableHeading,
                  name: filename,
                }, tableHeaders, newRows.data, type);
            }
            setExportInProgress(false);
          });
      }
    } else {
      const newRows = tableData.rows;
      return getFilenameWithDate(format)
        .then(async (filename) => {
          if (format === 'excel') {
            const generateExcel = await import(/* webpackChunkName: 'EnhancedTableExcelGenerator' */ './EnhancedTableExcelGenerator');
            generateExcel.default(
              {
                title: tableHeading,
                name: filename,
              }, tableHeaders, newRows, type);
          }
          setExportInProgress(false);
        });
    }
    setExportInProgress(false);
  };

  return (
    <div
      id={`${tableNm}-table-share-component`}
      style={{
        display: 'inline-block',
        marginRight: '10px',
        background: 'white',
        justifyContent: 'space-between',
      }}
    >
      <OverflowItem
        labelComponent={(
          <Tooltip
            id={`${tableNm}-table-share-button-tooltip`}
            triggerElement={(
              <TooltipButton
                id={`${tableNm}-table-share-button`}
                icon={<IconDownload size="md" theme={dlsBrightBlue} title="Download icon" filled={iconFilled} />}
                onMouseOver={() => {
                  setIconFilled(true);
                }}
                onMouseOut={() => {
                  setIconFilled(false);
                }}
                onClick={handleToolTipBtnClick}
              />
            )}
            placement="top"
            forcePlacement={true}
          >Export and More
          </Tooltip>
        )}
        width="311"
        isLabelComponentClicked={labelComponentClicked}
        setLabelComponentClicked={setLabelComponentClicked}
        id={`${tableNm}-table-share-button-overflow`}
        className={{ display: 'flex' }}
        toggleDirection={tableData?.exportPopupToggleDirection}
      >
        <div style={{
          backgroundColor: 'white',
          padding: '23px',
          border: '1px solid #C8C9C7',
        }}
        >
          <RadioGroup
            id={`${tableNm}-share-menu-type`}
            name={`${tableNm}-share-menu-radio-group`}
            defaultChecked={formData.type}
            onChange={(e) => handleOnChange(e.target.value, 'type')}
          >
            <Legend>
              Select Export Option
            </Legend>
            <RadioButton
              id={`${tableNm}-share-menu-type-current`}
              name={`${tableNm}-share-menu-current`}
              label="Export Current Columns"
              value="current"
            />
            <RadioButton
              id={`${tableNm}-share-menu-type-all`}
              name={`${tableNm}-share-menu-all`}
              // key={`${tableNm}-excel-export-menu-option`}
              label="Export All Columns"
              value="all"
            />
          </RadioGroup>

          <Label>
            Format
          </Label>
          <Select
            id="format"
            value={formData.format}
            // disabled={isDisabled}
            onChange={(e) => handleOnChange(e.target.value, 'format')}
          >
            {/* <SelectOption
              id="0"
              value="pdf"
            >
              Export As PDF
            </SelectOption> */}
            <SelectOption
              id="1"
              value="excel"
            >
              Export As Excel
            </SelectOption>

          </Select>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            margin: '20px 0px',
          }}
          >
            <Button
              styleType="secondary"
              size="sm"
              onClick={handleToolTipBtnClick}
            >
              Cancel

            </Button>
            <Button
              styleType="primary"
              size="sm"
              onClick={() => exportDataToFile()}
            >
              Export
            </Button>
          </div>
        </div>
      </OverflowItem>
      <Loader
        showLoader={exportInProgress}
      />
    </div>
  );
});

ShareComponent.displayName = 'ShareComponent';

ShareComponent.propTypes = {
  onExportClick: PropTypes.func,
  setItemActionClicked: PropTypes.func,
};
export default ShareComponent;
